/**
 * TutorialLayout — shared shell for in-depth walkthrough pages.
 *
 * Renders a sticky table-of-contents on the left, section content on the
 * right. Sections auto-anchor by id and support: text paragraphs, tip
 * boxes, warning boxes, step lists, feature callouts, and "Try it now"
 * CTAs that deep-link into the actual product feature.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import {
  ChevronRight, Info, AlertTriangle, CheckCircle2, ArrowLeft,
  Lightbulb, PlayCircle,
} from 'lucide-react';

const iconMap = { Info, Warning: AlertTriangle, Tip: Lightbulb, Success: CheckCircle2 };

const Callout = ({ kind = 'tip', children }) => {
  const map = {
    tip: { Icon: Lightbulb, cls: 'border-primary/40 bg-primary/5 text-foreground' },
    info: { Icon: Info, cls: 'border-sky-500/40 bg-sky-500/5 text-foreground' },
    warning: { Icon: AlertTriangle, cls: 'border-amber-500/50 bg-amber-500/10 text-foreground' },
    success: { Icon: CheckCircle2, cls: 'border-emerald-500/40 bg-emerald-500/5 text-foreground' },
  };
  const { Icon, cls } = map[kind] || map.tip;
  return (
    <div className={`rounded-2xl border ${cls} px-4 py-3 my-3 flex gap-3`}>
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-80" />
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
};

const StepList = ({ steps }) => (
  <ol className="my-3 space-y-2" data-testid="tutorial-steps">
    {steps.map((s, i) => (
      <li key={i} className="flex gap-3">
        <span
          className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center flex-shrink-0"
          aria-hidden="true"
        >
          {i + 1}
        </span>
        <div className="text-sm text-foreground leading-relaxed">{s}</div>
      </li>
    ))}
  </ol>
);

const TryItButton = ({ to, label }) => (
  <div className="my-3">
    <Button asChild className="rounded-full font-bold">
      <Link to={to} data-testid={`tutorial-try-${to.replace(/[^a-z0-9]+/gi, '-')}`}>
        <PlayCircle className="w-3.5 h-3.5 mr-1" /> {label || 'Try it now'}
      </Link>
    </Button>
  </div>
);

/**
 * TutorialLayout renders an array of `sections`. Each section is:
 *   { id: 'intro', title: 'Introduction', body: <JSX> }
 */
const TutorialLayout = ({ eyebrow, title, subtitle, sections, backTo, backLabel }) => {
  const [active, setActive] = useState(sections[0]?.id);

  // Scroll-spy: highlight the section closest to the top of viewport.
  useEffect(() => {
    const handler = () => {
      let current = sections[0]?.id;
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top < 140) current = s.id;
      }
      setActive(current);
    };
    window.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => window.removeEventListener('scroll', handler);
  }, [sections]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {backTo && (
        <Link
          to={backTo}
          className="inline-flex items-center gap-1 text-xs font-bold text-muted-foreground hover:text-foreground mb-3"
          data-testid="tutorial-back"
        >
          <ArrowLeft className="w-3 h-3" /> {backLabel || 'Back'}
        </Link>
      )}
      <div className="mb-6">
        <div className="eyebrow text-primary">{eyebrow}</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight mt-2">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-3 text-base text-muted-foreground max-w-2xl">{subtitle}</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* TOC */}
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-20 pro-card p-3" data-testid="tutorial-toc">
            <div className="eyebrow text-muted-foreground mb-2 px-1">Contents</div>
            <nav className="space-y-0.5">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={`tutorial-toc-${s.id}`}
                  className={`block rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${
                    active === s.id
                      ? 'bg-primary/10 text-primary'
                      : 'text-foreground/75 hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <ChevronRight className={`inline w-3 h-3 mr-1 -mt-0.5 transition-transform ${active === s.id ? 'rotate-90' : ''}`} />
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article className="lg:col-span-9 space-y-8" data-testid="tutorial-content">
          {sections.map((s) => (
            <section
              key={s.id}
              id={s.id}
              className="pro-card p-6 scroll-mt-24"
              data-testid={`tutorial-section-${s.id}`}
            >
              <h2 className="font-heading text-2xl font-extrabold text-foreground mb-3 tracking-tight">
                {s.title}
              </h2>
              <div className="prose prose-sm max-w-none dark:prose-invert
                              prose-p:my-2 prose-p:leading-relaxed prose-p:text-sm
                              prose-li:text-sm prose-li:my-0.5
                              prose-headings:font-heading prose-headings:tracking-tight
                              prose-h3:text-base prose-h3:font-bold prose-h3:mt-4 prose-h3:mb-1
                              prose-strong:font-bold prose-strong:text-foreground
                              prose-code:before:content-none prose-code:after:content-none
                              prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs">
                {s.body}
              </div>
            </section>
          ))}
        </article>
      </div>
    </div>
  );
};

export { Callout, StepList, TryItButton, iconMap };
export default TutorialLayout;
